/* eslint-disable react/prop-types */
import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation } from '@apollo/client';
import { Modal, connectModal, Button, Alert } from '@gsa/afp-component-library';
import { uniq } from 'lodash';

import AddSopLine from './add-sop-line';
import {
  useSIMakeModelProvider,
  CREATE_SOP_CONTRACT_LINE,
  NON_STANDARD_LINE_TYPES,
  SIMakeModelPanellProvider,
} from '../make-model/si-make-model-panel-provider';
import AddNonStandardLine from './add-non-standard-line';
import AddNonStandardLineMass from './add-non-standard-line-mass';
import AddNonStandardLineExpress from './add-non-standard-line-express';
import { useAddContractLineProvider } from './add-contract-line-provider';
import ModificationNumberInput from './modification-number-input';
import { isContractSOP } from '../../components/contract-helpers';

const renderStandardLine = (purchaseTypeCode, contractHeader) => {
  // enum('E','M','N','O','S','Y-proprietry')
  switch (purchaseTypeCode) {
    case 'S':
    case 'Y':
      return <AddSopLine contractHeader={contractHeader} />;
    case 'M':
      return <AddNonStandardLineMass contractHeader={contractHeader} />;
    case 'E':
      return <AddNonStandardLineExpress contractHeader={contractHeader} />;
    default:
      // 'N', 'O', 'Y'
      return <AddNonStandardLine contractHeader={contractHeader} />;
  }
};

// Seeds chassis visibility for SIMakeModelPanellProvider. Leaving both values
// undefined is meaningful: it hands control to the provider, which derives them
// from the selected SIN's REQ_CHASSIS_MOD tag.
const getChassisProps = (contractHeader) => {
  const isSOP = isContractSOP(contractHeader?.solicitation?.purchaseTypeCode);

  // Non-SOP: chassis is always shown, and always optional.
  if (!isSOP) return { showChassis: true, requiresChassis: false };

  // SOP that is not awarded in Fleet: no chassis at all.
  if (!contractHeader?.fleetAwarded)
    return { showChassis: false, requiresChassis: false };

  // Fleet-awarded SOP: the SIN's tag decides, so let the provider drive it.
  return {};
};

const AddLineForm = ({ contractHeader, createError }) => {
  const purchaseTypeCode = contractHeader?.solicitation?.purchaseTypeCode;
  const { getErrors, dirtyFields, setDirtyFields, modTag, setModTag } =
    useSIMakeModelProvider();

  const modTagError = getErrors()?.find((e) => e.field === 'modTag')?.message;

  return (
    <>
      <h1 className="title-l" aria-label="Add line item">
        Add line item
      </h1>
      {createError && (
        <Alert type="error" slim>
          {createError}
        </Alert>
      )}

      {renderStandardLine(purchaseTypeCode, contractHeader)}

      <div className="margin-y-4">
        <ModificationNumberInput
          value={modTag}
          onChange={({ target: { value } }) => {
            setModTag(value);
          }}
          onBlur={() => {
            setDirtyFields((prev) => uniq([...prev, 'modTag']));
          }}
          errorMessage={
            dirtyFields?.includes('modTag') && modTagError
              ? modTagError
              : undefined
          }
        />
      </div>
    </>
  );
};

const AddLineActions = ({
  contractHeader,
  onClose,
  onCreateSuccess,
  setCreateError,
}) => {
  const purchaseTypeCode = contractHeader?.solicitation?.purchaseTypeCode;
  const {
    getErrors,
    setDirtyFields,
    selectedStandardItem,
    bodyMake,
    bodyModel,
    chassisMake,
    chassisModel,
    allowCustom,
    bodyMakeInputValue,
    bodyModelInputValue,
    chassisMakeInputValue,
    chassisModelInputValue,
    modelYear,
    modTag,
  } = useSIMakeModelProvider();
  const {
    nonStandardLineType,
    manufacturerModelNumber,
    getManufacturerModelNumberErrors,
  } = useAddContractLineProvider();

  const [createNewContractLine, createNewContractLineState] = useMutation(
    CREATE_SOP_CONTRACT_LINE,
    {
      onCompleted: (data) => {
        setCreateError(undefined);
        if (data?.createNewContractLine?.id)
          onCreateSuccess(data.createNewContractLine);
        else setCreateError('Failed to create contract line');
      },
      onError: (error) => {
        setCreateError(error.message);
      },
    },
  );

  const addNewContractLine = useCallback(() => {
    const variables = {
      contractHeaderId: contractHeader?.contractHeaderId,
      standardItemId: parseInt(selectedStandardItem?.id, 10),
      makeCode: parseInt(bodyMake?.value, 10),
      modelCode: bodyModel?.value,
      modelYear: parseInt(modelYear, 10),
      modTag: modTag || undefined,
      manufacturerModelNumber,
    };

    if (chassisMake) {
      variables.chassisMakeCode = parseInt(chassisMake?.value, 10);
      variables.chassisModelCode = chassisModel?.value;
    }

    if (allowCustom) {
      variables.makeName = bodyMakeInputValue;
      variables.modelName = bodyModelInputValue;
      if (chassisMakeInputValue) {
        variables.chassisMakeName = chassisMakeInputValue;
        variables.chassisModelName = chassisModelInputValue;
      }
    }

    createNewContractLine({
      variables: {
        createNewContractLineInput: variables,
      },
    });
  }, [
    selectedStandardItem,
    bodyMake,
    bodyMakeInputValue,
    bodyModel,
    bodyModelInputValue,
    chassisMake,
    chassisMakeInputValue,
    chassisModel,
    chassisModelInputValue,
    modelYear,
    modTag,
    allowCustom,
    contractHeader?.contractHeaderId,
    createNewContractLine,
    manufacturerModelNumber,
  ]);

  const addLineItem = useCallback(() => {
    if (
      purchaseTypeCode === 'M' ||
      (purchaseTypeCode === 'N' &&
        nonStandardLineType ===
          NON_STANDARD_LINE_TYPES.MANUFACTURER_MODEL_NUMBER)
    ) {
      setDirtyFields(['standardItem', 'manufacturerModelNumber', 'modTag']);
      const errors = getErrors();
      const siError = errors?.find((e) => e.field === 'standardItem')?.message;
      const modError = errors?.find((e) => e.field === 'modTag')?.message;
      const mmnError = getManufacturerModelNumberErrors()?.find(
        (e) => e.field === 'manufacturerModelNumber',
      );
      if (!siError && !mmnError && !modError) {
        addNewContractLine();
      }
    } else {
      setDirtyFields([
        'standardItem',
        'bodyMake',
        'bodyModel',
        'chassisMake',
        'chassisModel',
        'modelYear',
        'modTag',
      ]);
      const errors = getErrors();
      if (!errors?.length) {
        addNewContractLine();
      }
    }
  }, [
    getErrors,
    addNewContractLine,
    manufacturerModelNumber,
    nonStandardLineType,
  ]);

  return (
    <>
      <Button
        data-testid="close-new-contract-line-btn"
        variant="unstyled"
        onClick={onClose}
        label="Cancel"
      />
      <Button
        data-testid="add-new-contract-line-btn"
        className="margin-left-2"
        type="button"
        variant="primary"
        onClick={addLineItem}
        label={
          createNewContractLineState.loading ? 'Adding...' : 'Add new line item'
        }
        disabled={createNewContractLineState.loading}
      />
    </>
  );
};

const AddContractLineModal = ({
  contractHeader,
  isOpen = false,
  onClose,
  onCreateSuccess,
}) => {
  const [createError, setCreateError] = useState();
  return (
    <SIMakeModelPanellProvider {...getChassisProps(contractHeader)}>
      <Modal
        id="add-new-contract-line-modal"
        variant="extra-large"
        isOpen={isOpen}
        onClose={onClose}
        actions={
          <AddLineActions
            contractHeader={contractHeader}
            onClose={onClose}
            onCreateSuccess={onCreateSuccess}
            setCreateError={setCreateError}
          />
        }
      >
        <AddLineForm
          contractHeader={contractHeader}
          createError={createError}
        />
      </Modal>
    </SIMakeModelPanellProvider>
  );
};

AddContractLineModal.propTypes = {
  contractHeader: PropTypes.shape({
    contractHeaderId: PropTypes.string,
    solicitation: PropTypes.shape({
      purchaseTypeCode: PropTypes.string,
    }),
  }).isRequired,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onCreateSuccess: PropTypes.func.isRequired,
};

export default connectModal(AddContractLineModal);
